import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import ConfirmDelete from "../ConfirmDelete";
import { createAxios } from "../../createAxios";
import { getUserId } from "../../utils/authSession";
import {
  useBanUserMutation,
  useUsersQuery,
} from "../../queries/useToeicQueries";

const searchFieldSx = {
  mb: 0,
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "999px",
    bgcolor: "var(--toeic-surface)",
    boxShadow: "none",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputAdornment-root": {
    width: 30,
    height: 30,
    maxHeight: 30,
    borderRadius: "999px",
    bgcolor: "#e8f6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    mt: "0 !important",
    mr: 0.75,
  },
  "& .MuiOutlinedInput-input": { py: 0, fontSize: 14, fontWeight: 500 },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

function Users() {
  const [keySearch, setKeySearch] = React.useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const userID = getUserId(currentUser);
  const axiosJWT = createAxios(currentUser);
  const { data: content = [] } = useUsersQuery(keySearch);
  const banUserMutation = useBanUserMutation();

  const handleDelete = async (id) => {
    await banUserMutation.mutateAsync({
      accessToken,
      userID,
      deleteID: id,
      axiosJWT,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (e) => {
    setKeySearch(e.target.value);
    setPage(1);
  };

  const formatDate = (dateString) => {
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-GB", options);
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        mb: 6,
      }}
    >
      <Box component="header" sx={{ maxWidth: 760 }}>
        <Typography
          component="h1"
          sx={{
            color: "var(--toeic-ink-deep)",
            fontSize: { xs: 32, md: 44 },
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 1.05,
          }}
        >
          All{" "}
          <Box
            component="span"
            sx={{
              color: "var(--toeic-sky-deep)",
              background: "linear-gradient(180deg, transparent 58%, rgba(70,168,232,.2) 0)",
            }}
          >
            Users
          </Box>
          .
        </Typography>
        <Typography sx={{ mt: 1, color: "var(--toeic-ink-soft)", fontWeight: 700, lineHeight: 1.7 }}>
          A cleaner user list with lighter search, softer rows, and clearer actions.
        </Typography>
      </Box>

      <Box sx={{ mt: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
        <Box
          className="toeic-reveal"
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "18px",
            border: "1px solid rgba(185, 220, 255, .75)",
            bgcolor: "var(--toeic-paper-grid)",
            boxShadow: "var(--toeic-shadow-card)",
            display: "grid",
            gap: 1.5,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Typography sx={{ color: "var(--toeic-ink)", fontSize: 13, fontWeight: 700, textTransform: "uppercase" }}>
              User directory
            </Typography>
            <Typography sx={{ color: "var(--toeic-sky-deep)", fontSize: 13, fontWeight: 700 }}>
              {content.length} accounts
            </Typography>
          </Box>
          <TextField
            label="Search User"
            variant="outlined"
            fullWidth
            sx={searchFieldSx}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--toeic-sky-deep)", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          mt: 4,
          width: "100%",
          borderRadius: "14px",
          border: 0,
          boxShadow: "var(--toeic-shadow-card)",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "var(--toeic-sky)" }}>
              <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                ID
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                Name
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                Birthday
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                Phone
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                Role
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                Image
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                Ban
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {content
              ?.slice((page - 1) * itemsPerPage, page * itemsPerPage)
              .map((row, index) => (
                <TableRow
                  key={row._id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#f3f6f9" : "#ffffff",
                    "&:hover": {
                      backgroundColor: "#e8f6ff",
                    },
                  }}
                >
                  <TableCell>{(page - 1) * itemsPerPage + index + 1}</TableCell>
                  <TableCell>{row.user_name}</TableCell>
                  <TableCell>{formatDate(row.user_birthday)}</TableCell>
                  <TableCell>{row.user_phone}</TableCell>
                  <TableCell>{row.isAdmin ? "Admin" : "User"}</TableCell>
                  <TableCell>
                    <img
                      src={row.user_avatar}
                      alt={row.user_name}
                      style={{
                        width: "100px",
                        height: "auto",
                        padding: "5px",
                        border: "1px solid #ddd",
                        borderRadius: "999px",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton>
                      <ConfirmDelete
                        onDelete={() => handleDelete(row._id)}
                        color="#F44336"
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        count={Math.ceil(content.length / itemsPerPage)}
        page={page}
        onChange={handleChangePage}
        sx={{ mt: 3, display: "flex", justifyContent: "center" }}
      />
    </Box>
  );
}

export default Users;

